import moment from 'moment';
import CodeMirror from 'codemirror';
import { ICustomHint, ICustomProperty, IOperatorReplacement, IFunctionObject, IDeclarationObject, IModelField } from '../../index.d';

interface IConfig {
  operators: string[];
  functions: IFunctionObject[];
  customHints: ICustomHint[];
  declarations: IDeclarationObject[];
  customProperties: ICustomProperty[];
  operatorReplacements: IOperatorReplacement[];
  models: IModelField[];
}

const Pos = CodeMirror.Pos;

function scriptHint(editor: CodeMirror.Editor, options: IConfig) {
  const cur = (editor as any).getCursor();
  const token = editor.getTokenAt(cur);

  return {
    list: getCompletions(token, options),
    from: Pos(cur.line, token.start),
    to: Pos(cur.line, token.start + token.string.length)
  };
}


function getCompletions(token: CodeMirror.Token, options: IConfig) {
  const found: Array<{ text: string, displayText: string, render?($li: HTMLLIElement): void }> = [];
  const start = token.string.toLowerCase();

  function matchStartString(str: string) {
    return start && str.toLowerCase().lastIndexOf(start.toLowerCase(), 0) === 0;
  }

  function maybeAdd(str: string) {
    if (matchStartString(str)) {
      found.push({ text: str, displayText: str });
    }
  }

  function maybeAddOperatorReplacement(operator: IOperatorReplacement) {
    const text = operator.code;
    if (matchStartString(operator.name) && !found.find((item: any) => item.text === text)) {
      found.push({ text, displayText: operator.name });
    }
  }

  function maybeAddCustomProperty(property: ICustomProperty) {
    const text = `{${property.id}}`;
    if (matchStartString(property.name) && !found.find((item: any) => item.text === text)) {
      found.push({ text, displayText: property.name });
    }
  }

  function maybeAddFunction(item: IFunctionObject) {
    const matched = matchStartString(item.code) || matchStartString(item.name);
    if (matched) {
      found.push({ text: item.hintReplacement || `${item.code}()`, displayText: item.name });
    }
  }

  function maybeAddDeclaration(declaration: IDeclarationObject) {
    const matched = matchStartString(declaration.code) || matchStartString(declaration.name);
    if (matched) {
      found.push({
        text: declaration.hintReplacement || generateDefaultDeclarationValue(declaration),
        displayText: declaration.name
      });
    }
  }

  function maybeAddCustomHint(hint: ICustomHint) {
    const matched = hint.test ? hint.test(token.string) : matchStartString(hint.text);
    if (matched) {
      const renderProp = hint.render! && 'render';
      found.push({
        text: `${hint.value}`,
        displayText: hint.text,
        [renderProp]: ($li) => {
          hint.render!($li, token.string);
        }
      });
    }
  }

  options.operators.forEach(maybeAdd);
  options.declarations.forEach(maybeAddDeclaration);
  options.functions.forEach(maybeAddFunction);
  options.customProperties.forEach(maybeAddCustomProperty);
  options.operatorReplacements.forEach(maybeAddOperatorReplacement);
  options.customHints.forEach(maybeAddCustomHint);

  const models: IFunctionObject[] = options.models.map(item => ({ name: item.fqn!, code: item.fqn! }));
  models.forEach(maybeAddFunction);

  return found;
}



export default function generateHint(config: IConfig) {
  return (editor: CodeMirror.Editor) => {
    return scriptHint(editor, config);
  };
}


// 生成默认的类型声明值
function generateDefaultDeclarationValue(declaration: IDeclarationObject) {
  let declarationValue = '';
  if (declaration.code === 'DateTime') {
    declarationValue = `"${moment().format()}"`;
  }
  else if (declaration.code === 'Date') {
    declarationValue = `"${moment().format('YYYY-MM-DD')}"`;
  }
  else if (declaration.code === 'Time') {
    declarationValue = `"${moment().format('HH:mm:ss')}"`;
  }
  return `${declaration.code}(${declarationValue})`;
}
